import { DiseaseChartDay } from '@app/shared/components/disease-insight-chart/models';
import {
  GetVirusMonitorAdvisoriesApiResponse,
  GetVirusMonitorAdvisoryApiResponse,
  IrrigationAPIResponse,
  VirusMonitorAdvisoryApiDto,
  VirusMonitorDayAPIDto,
  VirusMonitorResultAPIDto,
} from './api-models';
import {
  Irrigation,
  VirusMonitorDay,
  VirusMonitorRegistration,
  VirusMonitorResult,
} from './models';
import {
  CropFieldAdvice,
  DailyAdvice,
  VirusMonitorCropFieldRegistration,
  VirusSprayCategory,
} from '@app/shared/components/disease-overview-table.component/models/models';
import { getAdviceColorKey, getAdviceTranslationKey } from '../shared/helpers/advice-style.helper';

export const mapGetVirusMonitorAdvisoryResponse = (
  response: GetVirusMonitorAdvisoryApiResponse,
  fallbackCropYear: number,
): VirusMonitorRegistration => {
  const advisory = response.advisoryDto;

  if (!advisory) {
    throw new Error('Virus monitor advisory response did not contain advisory data.');
  }

  return mapVirusMonitorRegistration(advisory, fallbackCropYear);
};

export const mapGetVirusMonitorAdvisoriesResponse = (
  response: GetVirusMonitorAdvisoriesApiResponse,
  fallbackCropYear: number,
): VirusMonitorRegistration[] => mapVirusMonitorRegistrations(response.cropFields, fallbackCropYear);

const mapVirusMonitorRegistrations = (
  responses: VirusMonitorAdvisoryApiDto[],
  fallbackCropYear: number,
): VirusMonitorRegistration[] => responses.map((response) => mapVirusMonitorRegistration(response, fallbackCropYear));

const mapVirusMonitorRegistration = (
  response: VirusMonitorAdvisoryApiDto,
  fallbackCropYear: number,
): VirusMonitorRegistration => ({
  id: response.id,
  cropFieldId: response.cropFieldId,
  cropFieldName: response.cropFieldName,
  amountOfHectares: response.areaInHectares,
  cropId: response.cropId,
  cropName: response.cropName,
  cropVarietyName: response.cropVarietyName,
  cropYear: response.cropYear ?? response.year ?? fallbackCropYear,
  isVisible: response.isVisible,
  plantDate: response.plantDate ? new Date(response.plantDate) : undefined,
  plantDateCreatedAt: response.plantDateCreatedAt ? new Date(response.plantDateCreatedAt) : undefined,
  plantDateUpdatedAt: response.plantDateUpdatedAt ? new Date(response.plantDateUpdatedAt) : undefined,
  harvestDate: response.harvestDate ? new Date(response.harvestDate) : undefined,
  emergenceDate: response.emergenceDate ? new Date(response.emergenceDate) : undefined,
  isPlantDateConfirmed: response.isPlantDateConfirmed,
  deadFoliageDate: response.deadFoliageDate ? new Date(response.deadFoliageDate) : undefined,
  sprayThreshold: response.sprayThresholdPercentage,
  availableProtectionTypes: normalizeProtectionTypes(response.availableProtectionTypes),
  selectedProtectionTypes: normalizeProtectionTypes(response.selectedProtectionTypes),
  irrigations: mapIrrigationAPIResponses(response.irrigations),
  virusMonitorResult: mapResultAPIResponse(response.virusMonitorResult, response.sprayThresholdPercentage),
});

const mapIrrigationAPIResponses = (responses: IrrigationAPIResponse[] = []): Irrigation[] =>
  responses.map((response) => ({
    startDate: new Date(response.startDate),
    endDate: new Date(response.endDate),
    dosageMm: response.dosageMm,
  }));

const mapResultAPIResponse = (response: VirusMonitorResultAPIDto, sprayThreshold?: number): VirusMonitorResult => ({
  ...response,
  calculatedOn: new Date(response.calculatedOn),
  calculationStatus: response.calculationStatus,
  days: response.days ? response.days.map((day) => mapVirusMonitorDayAPIResponse(day, sprayThreshold)) : [],
  today: response.today ? mapVirusMonitorDayAPIResponse(response.today, sprayThreshold) : undefined,
});

export const mapVirusMonitorCropFieldAdvice = (registration: VirusMonitorRegistration): CropFieldAdvice => ({
  cropFieldId: registration.cropFieldId,
  cropFieldName: registration.cropFieldName,
  cropVarietyName: registration.cropVarietyName,
  amountOfHectares: registration.amountOfHectares,
  cropYear: registration.cropYear,
  emergenceDate: registration.emergenceDate,
  plantDate: registration.plantDate,
  harvestDate: registration.harvestDate,
  advices: registration.virusMonitorResult?.days?.map(mapAdvices) ?? [],
  todaysAdvice: registration.virusMonitorResult?.today ? mapAdvices(registration.virusMonitorResult.today) : undefined,
  lastSprayDate: getLastSprayDate(registration.virusMonitorResult?.days ?? []),
  isCropFieldVisible: true,
});

const mapAdvices = (day: VirusMonitorDay): DailyAdvice => ({
  date: new Date(day.date),
  isSprayedToday: !!day.sprayTime,
  translationKey: getAdviceTranslationKey(day),
  stylingOverride: getAdviceColorKey(day),
});

export const mapVirusMonitorDayAPIResponse = (response: VirusMonitorDayAPIDto, sprayThreshold?: number): VirusMonitorDay => ({
  ...response,
  date: new Date(response.date),
  sprayTime: response.sprayTime ? new Date(response.sprayTime) : undefined,
  sprayThreshold: sprayThreshold ?? response.sprayThresholdPercentage,
});

export const mapVirusMonitorDataToCropFieldRegistration = (
  data: VirusMonitorRegistration,
): VirusMonitorCropFieldRegistration => ({
  ...data,
  year: data.cropYear,
  plantDate: data.plantDate,
  plantDateCreatedAt: data.plantDateCreatedAt,
  plantDateUpdatedAt: data.plantDateUpdatedAt,
  emergenceDate: data.emergenceDate,
  harvestDate: data.harvestDate,
  isPlantDateConfirmed: data.isPlantDateConfirmed,
  isParticipatingToBem: data.isParticipatingToBem,
  deadFoliageDate: data.deadFoliageDate,
  sprayThreshold: data.sprayThreshold,
  availableProtectionTypes: data.availableProtectionTypes,
  selectedProtectionTypes: data.selectedProtectionTypes,
});

export function mapVirusMonitorDaysToChartDays(days: VirusMonitorDay[], irrigations: Irrigation[] = []): DiseaseChartDay[] {
  return days.map((day) => ({
    date: day.date,
    sprayTime: day.sprayTime,
    weather: (day.hourlyWeather ?? []).map((weather) => ({
      hour: toNumber(weather.hour) ?? 0,
      temperature: toNumber(weather.temperatureCelsius),
      humidity: toNumber(weather.relativeHumidityPercent),
      precipitation: toNumber(weather.precipitationMm),
      irrigation: getHourlyIrrigation(day.date, toNumber(weather.hour) ?? 0, irrigations),
    })),
    sprayThreshold: toNumber(day.sprayThreshold),
    infectionRiskPercentage: toNumber(day.riskLevel),
    uvIndex: toNumber(day.uvIndex),
    uvRadiationDose: toNumber(day.uvRadiationDoseJCm2),
    leafRollAphidCount: toNumber(day.leafRollVirusAphidCount),
    leafRollVirusRiskPercentage: toNumber(day.leafRollVirusRiskPercentage),
    oilProtectionPercent: toNumber(day.oilProtectionPercent),
    pyrethroidProtectionPercent: toNumber(day.pyrethroidProtectionPercent),
    systemicProtectionPercent: toNumber(day.systemicProtectionPercent),
    overallProtectionPercent: toNumber(day.overallProtectionPercent),
    adviceTranslationKey: getAdviceTranslationKey(day),
    adviceStylingOverride: getAdviceColorKey(day),
  }));
}

function getHourlyIrrigation(dayDate: Date, hour: number, irrigations: Irrigation[]): number | undefined {
  if (!irrigations.length) return undefined;

  const hourStart = Date.UTC(dayDate.getUTCFullYear(), dayDate.getUTCMonth(), dayDate.getUTCDate(), hour);
  const hourEnd = Date.UTC(dayDate.getUTCFullYear(), dayDate.getUTCMonth(), dayDate.getUTCDate(), hour + 1);

  const irrigationMm = irrigations.reduce((sum, irrigation) => {
    const irrigationStart = new Date(irrigation.startDate).getTime();
    const irrigationEnd = new Date(irrigation.endDate).getTime();
    const durationMs = irrigationEnd - irrigationStart;

    if (durationMs <= 0 || irrigation.dosageMm == null) {
      return sum;
    }

    const overlapStart = Math.max(hourStart, irrigationStart);
    const overlapEnd = Math.min(hourEnd, irrigationEnd);

    if (overlapEnd <= overlapStart) {
      return sum;
    }

    const overlapRatio = (overlapEnd - overlapStart) / durationMs;
    return sum + irrigation.dosageMm * overlapRatio;
  }, 0);

  return irrigationMm > 0 ? Number.parseFloat(irrigationMm.toFixed(3)) : undefined;
}

const toNumber = (value: number | string | undefined | null): number | undefined =>
  value == null ? undefined : Number(value);

const normalizeProtectionTypes = (values: Array<VirusSprayCategory | string> = []): VirusSprayCategory[] =>
  values
    .map((value) => {
      if (value === VirusSprayCategory.Systemic || value === VirusSprayCategory.Pyrethroid || value === VirusSprayCategory.Oil) {
        return value;
      }

      switch (value) {
        case 'Systemic':
          return VirusSprayCategory.Systemic;
        case 'Pyrethroid':
          return VirusSprayCategory.Pyrethroid;
        case 'Oil':
          return VirusSprayCategory.Oil;
        default:
          return undefined;
      }
    })
    .filter((value): value is VirusSprayCategory => value !== undefined);

const getLastSprayDate = (days: VirusMonitorDay[]): Date | undefined => {
  const sprayDates = days.map((day) => day.sprayTime).filter((sprayTime): sprayTime is Date => !!sprayTime);
  if (!sprayDates.length) {
    return undefined;
  }

  return new Date(Math.max(...sprayDates.map((date) => date.getTime())));
};
